import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { driver, type DriveStep, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  PRODUCT_TOUR_STEPS,
  PRODUCT_TOUR_STEP_COUNT,
} from '@/lib/onboarding/product-tour-steps';
import { showProductTourFarewell } from '@/lib/onboarding/product-tour-farewell';
import {
  activateProductTour,
  advanceProductTourStep,
  completeProductTour,
  getProductTourState,
  isProductTourActive,
  restartProductTour,
  skipProductTour,
} from '@/lib/onboarding/product-tour-state';
import { ProductTourContext } from './product-tour-context';

const matchTourRoute = (
  pathname: string,
  search: string,
  route: string
): boolean => {
  if (!route.includes('?')) {
    return pathname === route;
  }

  const [path, query] = route.split('?');
  return pathname === path && search.includes(query);
};

const waitForElement = (
  selector: string,
  attempts = 20,
  intervalMs = 150
): Promise<Element | null> =>
  new Promise((resolve) => {
    let tries = 0;

    const tick = () => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }

      tries += 1;
      if (tries >= attempts) {
        resolve(null);
        return;
      }

      window.setTimeout(tick, intervalMs);
    };

    tick();
  });

export const ProductTourProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const driverRef = useRef<Driver | null>(null);
  const farewellDriverRef = useRef<Driver | null>(null);
  const isAdvancingRef = useRef(false);
  const isTourNavigationRef = useRef(false);
  const farewellPendingRef = useRef(false);
  const [tick, setTick] = useState(0);
  const [active, setActive] = useState(isProductTourActive);

  const bump = useCallback(() => setTick((value) => value + 1), []);

  const destroyDriver = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
  }, []);

  const syncActive = useCallback(() => {
    setActive(isProductTourActive());
  }, []);

  const showTourFarewell = useCallback(async () => {
    if (farewellPendingRef.current) return;
    farewellPendingRef.current = true;
    destroyDriver();
    skipProductTour();
    syncActive();

    farewellDriverRef.current = await showProductTourFarewell(
      waitForElement,
      () => {
        farewellPendingRef.current = false;
        farewellDriverRef.current = null;
      }
    );
  }, [destroyDriver, syncActive]);

  const endTourEarly = useCallback(() => {
    isAdvancingRef.current = false;
    isTourNavigationRef.current = false;
    void showTourFarewell();
  }, [showTourFarewell]);

  const showStep = useCallback(
    async (stepIndex: number) => {
      const step = PRODUCT_TOUR_STEPS[stepIndex];
      if (!step) {
        completeProductTour();
        syncActive();
        return;
      }

      const element = await waitForElement(step.element);
      if (!element) {
        if (stepIndex < PRODUCT_TOUR_STEP_COUNT - 1) {
          advanceProductTourStep(stepIndex + 1);
          bump();
        } else {
          completeProductTour();
          syncActive();
        }
        return;
      }

      destroyDriver();

      const isLast = stepIndex === PRODUCT_TOUR_STEP_COUNT - 1;
      const driveStep: DriveStep = {
        element: step.element,
        popover: {
          title: step.title,
          description: step.description,
          side: step.side,
          align: step.align,
        },
      };

      const driverObj = driver({
        popoverClass: 'sellyt-driver-popover',
        showProgress: true,
        progressText: '{{current}} of {{total}}',
        showButtons: ['next', 'close'],
        nextBtnText: 'Next',
        doneBtnText: 'Next',
        allowClose: true,
        overlayClickBehavior: 'close',
        steps: [driveStep],
        onNextClick: () => {
          isAdvancingRef.current = true;
          driverObj.destroy();
          driverRef.current = null;

          if (isLast) {
            completeProductTour();
            syncActive();
            return;
          }

          const nextIndex = stepIndex + 1;
          const nextStep = PRODUCT_TOUR_STEPS[nextIndex];
          advanceProductTourStep(nextIndex);

          if (nextStep && !matchTourRoute(pathname, search, nextStep.route)) {
            isTourNavigationRef.current = true;
            navigate(nextStep.route);
          }

          bump();
        },
        onCloseClick: () => {
          driverObj.destroy();
          driverRef.current = null;
          endTourEarly();
        },
        onDestroyStarted: () => {
          if (isAdvancingRef.current) {
            isAdvancingRef.current = false;
            return;
          }
          if (!isProductTourActive()) return;
          driverRef.current = null;
          endTourEarly();
        },
        onDestroyed: () => {
          driverRef.current = null;
        },
      });

      driverRef.current = driverObj;
      driverObj.drive();
    },
    [bump, destroyDriver, endTourEarly, navigate, pathname, search, syncActive]
  );

  const restartTour = useCallback(() => {
    farewellPendingRef.current = false;
    farewellDriverRef.current?.destroy();
    farewellDriverRef.current = null;
    destroyDriver();
    restartProductTour();
    syncActive();
    if (pathname !== '/') {
      isTourNavigationRef.current = true;
      navigate('/');
    } else {
      bump();
    }
  }, [bump, destroyDriver, navigate, pathname, syncActive]);

  useEffect(() => {
    const state = getProductTourState();

    if (state.status === 'pending') {
      activateProductTour();
      syncActive();
    }

    if (!isProductTourActive()) return;

    const step = PRODUCT_TOUR_STEPS[state.stepIndex];
    if (!step) {
      completeProductTour();
      syncActive();
      return;
    }

    if (!matchTourRoute(pathname, search, step.route)) {
      if (isTourNavigationRef.current) {
        isTourNavigationRef.current = false;
        navigate(step.route);
        return;
      }

      void showTourFarewell();
      return;
    }

    const timer = window.setTimeout(() => {
      void showStep(state.stepIndex);
    }, 400);

    return () => {
      window.clearTimeout(timer);
      destroyDriver();
    };
  }, [
    destroyDriver,
    navigate,
    pathname,
    search,
    showStep,
    showTourFarewell,
    syncActive,
    tick,
  ]);

  return (
    <ProductTourContext.Provider value={{ isActive: active, restartTour }}>
      {children}
    </ProductTourContext.Provider>
  );
};
